import { clientPortalApi } from './api';
import type { ApiResponse } from '@/types';
import type { ClientUpdateDetail, ClientUpdateList } from '@/types/updates';

/**
 * Spec 28 §11.7 (stream abill): client updates in the portal (x-client-token through the shared base query; no CSRF).
 * Evidence files are fetched as blobs with the same header (fetchEvidenceObjectUrl), never through a URL with the token.
 */
export const updatesApi = clientPortalApi
  .enhanceEndpoints({ addTagTypes: ['ClientUpdates'] })
  .injectEndpoints({
    endpoints: builder => ({
      getClientUpdates: builder.query<ApiResponse<ClientUpdateList>, { projectId?: string | null; page?: number }>({
        query: ({ projectId, page }) => ({
          url: '/updates',
          params: { ...(projectId ? { project_id: projectId } : {}), ...(page ? { page } : {}) },
        }),
        providesTags: ['ClientUpdates'],
      }),
      getClientUpdate: builder.query<ApiResponse<ClientUpdateDetail>, string>({
        query: id => `/updates/${encodeURIComponent(id)}`,
        providesTags: (_r, _e, id) => [{ type: 'ClientUpdates', id }],
      }),
    }),
  });

export const { useGetClientUpdatesQuery, useGetClientUpdateQuery } = updatesApi;

const apiBase = (): string => import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/client-portal';

/** The evidence file (or thumbnail) as an object URL. The token travels in the header only, never in the URL. */
export async function fetchEvidenceObjectUrl(updateId: string, evidenceId: string, thumbnail = false): Promise<string> {
  const token = localStorage.getItem('clientToken');
  const url = `${apiBase()}/updates/${encodeURIComponent(updateId)}/evidence/${encodeURIComponent(evidenceId)}${thumbnail ? '/thumbnail' : ''}`;
  const response = await fetch(url, { headers: token ? { 'x-client-token': token } : {} });
  if (!response.ok) throw new Error(`evidence ${response.status}`);
  return URL.createObjectURL(await response.blob());
}
